import { Controller } from "@hotwired/stimulus"
import RequestHelper from "../helpers/request_helper"
import ProgressBar from "../helpers/progress_bar"

// Initializes DataTables directly with its own dom/language/fnDrawCallback rather than going
// through the shared `datatable` controller. This page has no select2 or text filters, so
// `reload()` below is a plain re-fetch of the same source url.
//
// The row checkboxes, the select-all checkbox and the create/export/download buttons are all
// wired through data-action in the markup that renders them -- the static view (index.html.haml,
// the two action-button partials it renders) and the per-row checkbox (built exclusively by
// ShippingLabelDecorator#datatable_checkbox for GiftShippingLabelDatatable#data) alike. Where this
// controller rebuilds one of those same elements client-side after an ActionCable update, the
// rebuilt markup carries the identical data-action so it self-wires the same way (patterns.md #3,
// #7, #10).
const ACTION_BUTTON_IDS = [
  "create-shipping-labels",
  "download-shipping-labels",
  "export-shipping-labels",
  "print-packing-slips"
]

export default class extends Controller {
  static get targets() {
    return [
      "table", "recordsCount", "progressBanner", "errorBanner", "createHolder", "downloadHolder",
      "printPackingSlips", "hiddenGiftIds", "checkbox"
    ]
  }

  static get values() {
    return {
      source: String,
      pageLength: { type: Number, default: 25 }
    }
  }

  connect() {
    // fnDrawCallback is a DataTables plugin hook -- DataTables invokes it directly as a bare
    // function reference, not through a DOM event, so there's no data-action equivalent and the
    // pre-bound reference stays necessary.
    this.onDraw = this.onDraw.bind(this)

    // Captured once: later query-string rewrites always start from this original href, so
    // repeated toggles never accumulate more than one "?".
    this.originalPackingSlipUrl = this.hasPrintPackingSlipsTarget ? this.printPackingSlipsTarget.href : ""

    this.dataTable = $(this.tableTarget).dataTable({
      processing: true,
      serverSide: true,
      responsive: true,
      order: [[3, "asc"]],
      ajax: { url: this.sourceValue },
      pageLength: this.pageLengthValue || 25,
      autoWidth: true,
      dom: "<'row'<'col-sm-12'f>>" +
        "<'row'<'col-sm-12'tr>>" +
        "<'row'<'col-sm-5'i><'col-sm-7'p>>",
      pagingType: "full_numbers",
      language: {
        search: "",
        emptyTable: "No data available.",
        searchPlaceholder: "ID, SKU, or Name"
      },
      columns: [
        { data: "checkbox", orderable: false, searchable: false, className: "supplier-checkbox" },
        { data: "recipient_name", className: "recipient-name" },
        { data: "recipient_address", orderable: false, searchable: false, className: "recipient-address" },
        { data: "order_date", searchable: false, className: "order-date" },
        { data: "selected_product", orderable: true, className: "selected-product" },
        { data: "sku", orderable: false, className: "sku" },
        { data: "tracking_number", searchable: false, orderable: false, className: "tracking-number" },
        { data: "status", searchable: false, orderable: true, className: "status" },
        { data: "gift_id", searchable: false, className: "hidden" },
        { data: "links", orderable: false, searchable: false, className: "links" }
      ],
      fnDrawCallback: this.onDraw
    })

    // Resumes a subscription left running by a previous page load. Only the create flow shows the
    // page-wide progress banner on resume; the download flow deliberately does not. Both callback
    // references below are plain function arguments passed into a helper method, invoked later as
    // a bare call rather than through any DOM event -- pre-binding to the instance stays necessary
    // since there's no data-action equivalent for a callback that isn't wired to an event at all.
    if (this.hasCreateHolderTarget &&
        this.subscribeToInProgressEvent(this.createHolderTarget, this.subscribeToCreateLabelsEvent.bind(this))) {
      this.displayProgressBanner()
    }
    if (this.hasDownloadHolderTarget) {
      this.subscribeToInProgressEvent(this.downloadHolderTarget, this.subscribeToDownloadLabelsEvent.bind(this))
    }
  }

  disconnect() {
    if ($.fn.DataTable.isDataTable(this.tableTarget)) {
      $(this.tableTarget).DataTable().destroy()
    }
  }

  onDraw(settings) {
    const wrapper = settings.nTableWrapper

    if (settings._iDisplayLength > settings.fnRecordsDisplay()) {
      this.togglePaginationChrome(wrapper, false)
    } else {
      // perPageDropdownTemplate's own comment covers why a non-literal argument is safe here: the
      // string it returns has no interpolated value at all.
      wrapper.querySelector(".dataTables_info").insertAdjacentHTML("afterbegin", this.perPageDropdownTemplate())
      wrapper.querySelector("select[name=per]").value = settings._iDisplayLength
      this.togglePaginationChrome(wrapper, true)
    }

    this.setRecordsCount(settings.fnRecordsTotal())
  }

  // `wrapper` is `settings.nTableWrapper`, a plain node DataTables maintains internally -- plain
  // DOM work on the plugin's own generated chrome, not a plugin call itself, so it retreats to
  // native. jQuery 3's `.show()` writes the tag's default display back in explicitly rather than
  // clearing the inline style (pitfalls.md/patterns.md #11); neither `.dataTables_paginate` nor
  // `.dataTables_info` has a stylesheet rule to lose to on this page, but setting "block" reproduces
  // the same behavior unconditionally rather than relying on that staying true.
  togglePaginationChrome(wrapper, visible) {
    wrapper.querySelectorAll(".dataTables_paginate, .dataTables_info").forEach((el) => {
      this.setElementVisible(el, visible)
    })
  }

  onSelectAllChange(event) {
    this.toggleAllCheckboxes(event.currentTarget.checked)
    this.appendPrintingPackingSlipsQueryString()
    this.changeSelectedGiftIdsValue()
    this.toggleActionButtons(this.countCheckedRecords())
  }

  onCheckboxChange() {
    this.appendPrintingPackingSlipsQueryString()
    this.changeSelectedGiftIdsValue()
    this.toggleActionButtons(this.countCheckedRecords())
  }

  onPerPageChange(event) {
    const perPage = parseInt(event.currentTarget.value, 10)
    this.dataTable.DataTable().page.len(perPage)
    this.reload()
  }

  reload() {
    ProgressBar.start()
    $(this.tableTarget).DataTable().ajax.url(this.sourceValue).load(() => ProgressBar.stop())
  }

  onCreateClick(event) {
    event.preventDefault()
    event.currentTarget.setAttribute("disabled", "disabled")

    const ids = this.getSelectedGiftIds()
    const url = document.getElementById("bulk-create-form").action

    this.createHolderTarget.replaceChildren(this.buildEventInProgressMarkup(0, "Creating Shipping Labels"))
    this.displayProgressBanner()

    const options = RequestHelper.defaultPostOptions()
    options.headers = Object.assign({}, options.headers, { "Content-Type": "application/json" })
    options.body = JSON.stringify({ gift_ids: ids })

    fetch(url, options).
      then((response) => response.json()).
      then((data) => this.subscribeToCreateLabelsEvent(data.supplier_event_id)).
      catch(this.logFailedCall)
  }

  onExportClick(event) {
    event.preventDefault()
    event.currentTarget.setAttribute("disabled", "disabled")

    const ids = this.getSelectedGiftIds()
    const format = document.getElementById("download_format").value
    const url = document.getElementById("bulk-download-form").action

    this.downloadHolderTarget.replaceChildren(this.buildEventInProgressMarkup(0, "Exporting Shipping Labels"))

    const options = RequestHelper.defaultPostOptions()
    options.headers = Object.assign({}, options.headers, { "Content-Type": "application/json" })
    options.body = JSON.stringify({ gift_ids: ids, download_format: format })

    fetch(url, options).
      then((response) => response.json()).
      then((data) => this.subscribeToDownloadLabelsEvent(data.supplier_event_id)).
      catch(this.logFailedCall)
  }

  onDownloadClick() {
    this.downloadHolderTarget.replaceChildren(this.buildUnstartedLink({
      url: "",
      id: "export-shipping-labels",
      label: "Export Shipping Labels",
      dataAction: "click->shipping-labels#onExportClick"
    }))
  }

  subscribeToInProgressEvent(holder, subscriptionFunc) {
    const event = this.currentEvent(holder)
    if (!event) { return false }

    if (event.stage === "waiting" || event.stage === "in_progress") {
      subscriptionFunc(event.id)
      return true
    }

    return false
  }

  // `data-event` carries the SupplierEvent as JSON, the same shape jQuery's data cache used to
  // auto-parse for us. Read off the holder target, not the controller's own root element, so it
  // isn't a Values API candidate (decisions.md D13).
  currentEvent(holder) {
    const raw = holder.getAttribute("data-event")
    return raw ? JSON.parse(raw) : null
  }

  subscribeToCreateLabelsEvent(eventId) {
    const controller = this
    const subscription = App.cable.subscriptions.create({ channel: "CreateShippingLabelsChannel", id: eventId }, {
      received(data) {
        if (data.event.stage === "in_progress" || data.event.stage === "waiting") {
          controller.updateProgressBanner(data.event, false)
          controller.setProgressBarWidth(controller.createHolderTarget, `${data.event.progress}%`)
        } else if (data.event.stage === "complete") {
          controller.updateProgressBanner(data.event, true)
          controller.setProgressBarWidth(controller.createHolderTarget, "100%")
          subscription.unsubscribe()
          $(controller.tableTarget).DataTable().ajax.reload()
          controller.createHolderTarget.replaceChildren(controller.buildCompletedLink({
            url: data.event.url,
            id: "create-shipping-labels",
            label: "Create Shipping Labels",
            disabled: true,
            dataAction: "click->shipping-labels#onCreateClick"
          }))
        } else if (data.event.stage === "failed") {
          subscription.unsubscribe()
          controller.createHolderTarget.replaceChildren(controller.buildCompletedLink({
            url: data.event.url,
            id: "create-shipping-labels",
            label: "Create Shipping Labels",
            disabled: true,
            dataAction: "click->shipping-labels#onCreateClick"
          }))
          controller.hideProgressBanner()
          controller.displayFatalErrorBanner("A fatal error has occurred when creating the shipping labels. Please contact support.")
        }
      }
    })
  }

  subscribeToDownloadLabelsEvent(eventId) {
    const controller = this
    const subscription = App.cable.subscriptions.create({ channel: "DownloadShippingLabelsChannel", id: eventId }, {
      received(data) {
        if (data.event.stage === "in_progress" || data.event.stage === "waiting") {
          controller.setProgressBarWidth(controller.downloadHolderTarget, `${data.event.progress}%`)
        } else if (data.event.stage === "complete") {
          controller.setProgressBarWidth(controller.downloadHolderTarget, "100%")
          subscription.unsubscribe()
          controller.downloadHolderTarget.replaceChildren(controller.buildCompletedLink({
            url: data.event.url,
            id: "download-shipping-labels",
            label: "Download Shipping Labels",
            disabled: false,
            dataAction: "click->shipping-labels#onDownloadClick"
          }))
        } else {
          controller.displayFatalErrorBanner(data.event.error)
        }
      }
    })
  }

  // Scoped off the holder target rather than a bare `#…-holder .progress-bar-info` selector --
  // same element either way (the holder's own id), just found through the target instead of a
  // repeated global lookup. A holder not currently showing a progress bar (e.g. between states)
  // silently no-ops, same as the jQuery call this replaces.
  setProgressBarWidth(holder, width) {
    const bar = holder.querySelector(".progress-bar-info")
    if (bar) { bar.style.width = width }
  }

  toggleActionButtons(display) {
    ACTION_BUTTON_IDS.forEach((id) => {
      const el = document.getElementById(id)
      if (!el) { return }

      if (display) {
        el.removeAttribute("disabled")
      } else {
        el.setAttribute("disabled", "disabled")
      }
    })
  }

  toggleAllCheckboxes(display) {
    this.checkboxTargets.forEach((checkbox) => {
      checkbox.checked = display
    })
  }

  countCheckedRecords() {
    return this.checkboxTargets.filter((checkbox) => checkbox.checked).length
  }

  getSelectedGiftIds() {
    // Each row's gift id lives on its own checkbox target, not on the controller's own root
    // element, so it stays a plain attribute read rather than a Values API candidate (D13) --
    // the same reasoning campaign_dashboard's per-widget url read documents (patterns.md #13).
    return this.checkboxTargets.filter((checkbox) => checkbox.checked).map((checkbox) => checkbox.dataset.giftid)
  }

  buildSelectedGiftIdsValue() {
    return this.getSelectedGiftIds().join(",")
  }

  buildQueryStringParams() {
    return this.getSelectedGiftIds().map((id) => `gift_id[]=${id}`).join("&")
  }

  appendPrintingPackingSlipsQueryString() {
    if (!this.hasPrintPackingSlipsTarget) { return }

    this.printPackingSlipsTarget.href = `${this.originalPackingSlipUrl}?${this.buildQueryStringParams()}`
  }

  changeSelectedGiftIdsValue() {
    if (!this.hasHiddenGiftIdsTarget) { return }

    this.hiddenGiftIdsTarget.value = this.buildSelectedGiftIdsValue()
  }

  setRecordsCount(count) {
    this.recordsCountTarget.innerText = count
  }

  displayProgressBanner() {
    this.progressBannerTarget.textContent = "Loading information..."
    this.setElementVisible(this.progressBannerTarget, true)
  }

  hideProgressBanner() {
    this.setElementVisible(this.progressBannerTarget, false)
  }

  displayFatalErrorBanner(message) {
    this.errorBannerTarget.textContent = message
    this.setElementVisible(this.errorBannerTarget, true)
  }

  // jQuery 3's `.show()`/`.hide()` write an explicit inline `display` rather than merely clearing
  // it, because clearing would let a same-specificity stylesheet rule reassert itself through the
  // cascade -- `#progress-banner-container`/`#fatal-error-banner-container` both have a plain
  // `display: none` rule in shipping-labels.scss, so a naive `""` conversion would stop them ever
  // showing again (patterns.md #11).
  setElementVisible(el, visible) {
    el.style.display = visible ? "block" : "none"
  }

  // `total`/`complete`/`failed`/`pending` arrive over the wire from BuyShippingLabelJob's
  // ActionCable broadcast -- this controller's own JS builds the markup client-side rather than
  // trusting a formatted string, same treatment as the completed/in-progress links below.
  updateProgressBanner({ total, complete, failed, pending }, final) {
    const fragment = document.createDocumentFragment()

    if (final) {
      fragment.append(
        this.numberSpan(complete), document.createTextNode(" shipping labels were created. "),
        this.numberSpan(failed), document.createTextNode(" have failed. ")
      )
    } else {
      fragment.append(
        this.numberSpan(complete), document.createTextNode(" of "),
        this.numberSpan(total), document.createTextNode(" shipping labels were created and "),
        this.numberSpan(pending), document.createTextNode(` ${pending === 1 ? "is" : "are"} still processing. `)
      )
    }

    fragment.append(
      document.createTextNode("Go to "),
      this.numberSpan("Pending Fulfillment"),
      document.createTextNode(" tab to print shipping labels.")
    )

    this.progressBannerTarget.replaceChildren(fragment)
  }

  numberSpan(value) {
    const span = document.createElement("span")
    span.className = "number"
    span.textContent = value
    return span
  }

  // Builds the same progress-bar markup `suppliers/shipping_labels/_create_shipping_labels_button`
  // and `_download_and_print_shipping_labels_buttons` render server-side for a page load that
  // resumes mid-event, as DOM nodes rather than an innerHTML-assigned string.
  buildEventInProgressMarkup(progress, label) {
    const wrapper = document.createElement("div")
    wrapper.className = "progress exporter-progress"

    const bar = document.createElement("div")
    bar.setAttribute("aria-valuemax", "100")
    bar.setAttribute("aria-valuemin", "0")
    bar.setAttribute("aria-valuenow", String(progress))
    bar.className = "progress-bar progress-bar-info progress-bar-striped active"
    bar.setAttribute("role", "progressbar")
    bar.style.width = `${progress}%`

    const idle = document.createElement("div")
    idle.className = "progress-bar progress-bar-idle progress-bar-striped active"
    idle.style.width = "100%"

    const labelSpan = document.createElement("span")
    labelSpan.textContent = `${label}...`

    wrapper.append(bar, idle, labelSpan)
    return wrapper
  }

  // `url` is carrier/event data read off the ActionCable payload (the create flow never actually
  // broadcasts one, so it renders "undefined" there exactly as the old string template did --
  // harmless since the link stays `disabled` and its own click handler always calls
  // preventDefault; the download flow's `url` is `exported_shipping_labels_path`, generated by our
  // own route helpers). Building the anchor as a node rather than interpolating into a string means
  // a value here can never be read back out as markup.
  buildCompletedLink({ url, id, label, disabled, dataAction }) {
    const link = document.createElement("a")
    link.target = "_blank"
    link.id = id
    link.className = "btn btn-primary hidden-xs"
    link.setAttribute("href", url)
    if (disabled) { link.setAttribute("disabled", "disabled") }
    if (dataAction) { link.setAttribute("data-action", dataAction) }
    link.textContent = label
    return link
  }

  buildUnstartedLink({ url, id, label, dataAction }) {
    const link = document.createElement("a")
    link.className = "btn btn-primary"
    link.setAttribute("href", url)
    link.id = id
    if (dataAction) { link.setAttribute("data-action", dataAction) }
    link.textContent = label
    return link
  }

  // Purely static -- no interpolated value reaches this string -- so it stays a template inserted
  // via insertAdjacentHTML rather than needing a DOM-node builder. The data-action lands on the
  // element in the template itself (patterns.md #7): DataTables regenerates `.dataTables_info` on
  // every draw, so this select is a fresh node each time, and Stimulus's MutationObserver wires it
  // up again exactly the same way regardless of the (now-native) insertion mechanism.
  perPageDropdownTemplate() {
    return `
      <label>
        Show
        <select class="form-control push-r-10 push-l-5" name="per" data-action="change->shipping-labels#onPerPageChange">
          <option value="25">25 rows</option>
          <option value="50">50 rows</option>
          <option value="75">75 rows</option>
          <option value="100">100 rows</option>
          <option value="500">500 rows</option>
          <option value="1000">1000 rows</option>
        </select>
      </label>
    `
  }

  logFailedCall(response) {
    console.warn(response)
  }
}
